import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { scheduleBlocksService, userService } from '../services/api';
import toast from 'react-hot-toast';

const ScheduleBlocks = ({ currentUser, onClose, onSwitchTab }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, blockId: null });
  const [filter, setFilter] = useState({
    type: '',
    date_from: '',
    date_to: ''
  });

  const { data: blocks = [], isLoading, error } = useQuery({
    queryKey: ['schedule-blocks', filter],
    queryFn: async () => {
      const isGerenciaOuAgenda = currentUser?.role === 'GERENCIA' || currentUser?.role === 'RECEPCAO_COMPRAS_AGENDA';
      const params = { ...filter, my_blocks_only: !isGerenciaOuAgenda };
      const response = await scheduleBlocksService.getBlocks(params);
      return response.data?.data || [];
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await scheduleBlocksService.deleteBlock(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['schedule-blocks']);
    }
  });

  const handleEdit = (block) => {
    setEditingBlock(block);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ show: true, blockId: id });
  };

  const confirmDelete = () => {
    if (deleteConfirm.blockId) {
      deleteMutation.mutate(deleteConfirm.blockId);
      setDeleteConfirm({ show: false, blockId: null });
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
    return localDate.toLocaleDateString('pt-BR');
  };

  const formatTime = (time) => {
    if (!time) return 'Dia inteiro';
    return time.substring(0, 5);
  };

  const getBlockTypeLabel = (type) => {
    const labels = {
      'ADMINISTRATIVO': 'Administrativo',
      'PESSOAL': 'Pessoal',
      'EXTRAORDINARIO': 'Extraordinário'
    };
    return labels[type] || type;
  };

  const getBlockTypeColor = (type) => {
    const colors = {
      'ADMINISTRATIVO': 'bg-blue-100 text-blue-800 border-blue-300',
      'PESSOAL': 'bg-green-100 text-green-800 border-green-300',
      'EXTRAORDINARIO': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          🔒 Meus Bloqueios de Agenda
        </h2>
        <div className="flex gap-2">
          {currentUser?.role === 'GERENCIA' && (
            <button
              onClick={() => onSwitchTab('permissions')}
              className="px-3 py-2 text-sm bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200"
            >
              ⚙️ Permissões
            </button>
          )}
          <button
            onClick={() => {
              setEditingBlock(null);
              setShowForm(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Novo Bloqueio
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
          <select
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="">Todos</option>
            <option value="ADMINISTRATIVO">Administrativo</option>
            <option value="PESSOAL">Pessoal</option>
            <option value="EXTRAORDINARIO">Extraordinário</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data De</label>
          <input
            type="date"
            value={filter.date_from}
            onChange={(e) => setFilter({ ...filter, date_from: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data Até</label>
          <input
            type="date"
            value={filter.date_to}
            onChange={(e) => setFilter({ ...filter, date_to: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>

      {/* Lista de Bloqueios */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Carregando...</div>
      ) : error ? (
        <div className="text-center py-8 text-red-500">Erro ao carregar bloqueios</div>
      ) : blocks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Nenhum bloqueio encontrado
        </div>
      ) : (
        <div className="space-y-3">
          {blocks.map((block) => (
            <div
              key={block.id}
              className={`p-4 rounded-lg border-2 ${getBlockTypeColor(block.block_type)}`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold">📅 {formatDate(block.block_date)}</span>
                    <span className="text-sm px-2 py-0.5 bg-white rounded border">
                      {getBlockTypeLabel(block.block_type)}
                    </span>
                    {block.is_recurring && (
                      <span className="text-sm bg-white px-2 py-0.5 rounded border">
                        🔄 Recorrente
                      </span>
                    )}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Horário:</span> {formatTime(block.start_time)} - {formatTime(block.end_time)}
                  </div>
                  {block.description && (
                    <div className="text-sm mt-1">{block.description}</div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(block)}
                    className="px-3 py-1 text-sm bg-white rounded hover:bg-gray-100"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(block.id)}
                    className="px-3 py-1 text-sm bg-white rounded hover:bg-red-100"
                  >
                    Excluir
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmação de Exclusão */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold mb-4">Confirmar Exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja remover este bloqueio?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm({ show: false, blockId: null })}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Formulário */}
      {showForm && (
        <ScheduleBlockForm
          block={editingBlock}
          currentUser={currentUser}
          onClose={() => {
            setShowForm(false);
            setEditingBlock(null);
          }}
          onSuccess={() => {
            setShowForm(false);
            setEditingBlock(null);
            queryClient.invalidateQueries(['schedule-blocks']);
          }}
        />
      )}
    </div>
  );
};

const ScheduleBlockForm = ({ block, currentUser, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    user_id: block?.user_id || currentUser?.id,
    block_type: block?.block_type || 'PESSOAL',
    block_date: block?.block_date || '',
    start_time: block?.start_time?.substring(0, 5) || '',
    end_time: block?.end_time?.substring(0, 5) || '',
    is_full_day: !block?.start_time && !block?.end_time,
    is_recurring: block?.is_recurring || false,
    recurrence_pattern: block?.recurrence_pattern || 'WEEKLY',
    recurrence_end_date: block?.recurrence_end_date || '',
    description: block?.description || ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { data: usersData, isLoading: isLoadingUsers, isError: isUsersError } = useQuery({
    queryKey: ['users-for-block'],
    queryFn: async () => {
      console.log('[ScheduleBlockForm] Fetching users...');
      try {
        const response = await userService.getUsers({ active: 'true', limit: 100 });
        console.log('[ScheduleBlockForm] Users response:', response);
        return response;
      } catch (err) {
        console.error('[ScheduleBlockForm] Error fetching users:', err);
        throw err;
      }
    },
    enabled: currentUser?.role === 'GERENCIA'
  });

  console.log('[ScheduleBlockForm] currentUser:', currentUser?.role, '| usersData:', usersData, '| isLoading:', isLoadingUsers);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        block_type: formData.block_type,
        // Enviar data no formato YYYY-MM-DD sem conversão de timezone
        // O backend deve interpretar como data local (UTC-3)
        block_date: formData.block_date,
        start_time: formData.is_full_day ? null : formData.start_time || null,
        end_time: formData.is_full_day ? null : formData.end_time || null,
        is_recurring: formData.is_recurring,
        recurrence_pattern: formData.is_recurring ? formData.recurrence_pattern : null,
        recurrence_end_date: formData.is_recurring ? formData.recurrence_end_date : null,
        description: formData.description || null,
        user_id: currentUser?.role === 'GERENCIA' ? formData.user_id : currentUser?.id
      };

      console.log('[ScheduleBlockForm] Submitting block:', payload);

      const response = block
        ? await scheduleBlocksService.updateBlock(block.id, payload)
        : await scheduleBlocksService.createBlock(payload);

      if (response.data?.message) {
        toast.success(response.data.message);
      } else {
        toast.success('Bloqueio salvo com sucesso!');
      }
      onSuccess();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao salvar bloqueio';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">
          {block ? '✏️ Editar Bloqueio' : '🔒 Novo Bloqueio'}
        </h3>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {currentUser?.role === 'GERENCIA' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Usuário {isLoadingUsers && <span className="text-gray-400">(carregando...)</span>}
              </label>
              <select
                value={formData.user_id || ''}
                onChange={(e) => setFormData({ ...formData, user_id: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg"
                required
                disabled={isLoadingUsers || isUsersError}
              >
                <option value="">Selecione...</option>
                {isUsersError && <option value="">Erro ao carregar</option>}
                {usersData?.data?.data?.users?.map(user => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={formData.block_type}
              onChange={(e) => setFormData({ ...formData, block_type: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              required
            >
              <option value="ADMINISTRATIVO">Serviços Administrativos</option>
              <option value="PESSOAL">Compromisso Pessoal</option>
              <option value="EXTRAORDINARIO">Compromisso Extraordinário</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
            <input
              type="date"
              value={formData.block_date}
              onChange={(e) => setFormData({ ...formData, block_date: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              required
              min={new Date().toLocaleDateString('en-CA')} // YYYY-MM-DD no timezone (UTC-3 local)
            />
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_full_day}
                onChange={(e) => setFormData({ ...formData, is_full_day: e.target.checked })}
              />
              <span className="text-sm font-medium text-gray-700">Dia inteiro</span>
            </label>
          </div>

          {!formData.is_full_day && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Das</label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Até</label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
          )}

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_recurring}
                onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
              />
              <span className="text-sm font-medium text-gray-700">Recorrente</span>
            </label>
          </div>

          {formData.is_recurring && (
            <div className="space-y-2 pl-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Padrão</label>
                <select
                  value={formData.recurrence_pattern}
                  onChange={(e) => setFormData({ ...formData, recurrence_pattern: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="DAILY">Diário (todo dia)</option>
                  <option value="WEEKLY">Semanal</option>
                  <option value="BIWEEKLY">Quinzenal</option>
                  <option value="MONTHLY">Mensal</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Até</label>
                <input
                  type="date"
                  value={formData.recurrence_end_date}
                  onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  min={formData.block_date}
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              rows={2}
              placeholder="Ex: Reunião de equipe, Férias, Manutenção..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleBlocks;
export { ScheduleBlockForm };